import { useEffect, useState } from "react";
import axios from "axios";
import { Star, PenLine } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { REVIEWS } from "@/lib/site-data";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { ReviewDialog } from "@/components/site/ReviewDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reviews() {
  const [reviews, setReviews] = useState(REVIEWS);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((res) => {
      if (Array.isArray(res.data) && res.data.length) {
        setReviews([...res.data, ...REVIEWS]);
      }
    }).catch(() => {});
  }, []);

  return (
    <section id="reviews" className="relative py-24 sm:py-32 bg-[#F5F5F7] border-y border-black/5">
      <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <div className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#E31B23]">
            <span className="h-px w-8 bg-[#E31B23]" /> Trusted by shippers
          </div>
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-tight text-[#1F1F1F] sm:text-5xl">
              Loved by the customers who never stop shipping.
            </h2>
            <button data-testid="leave-review-btn" onClick={() => setOpen(true)}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#E31B23] hover:bg-[#B71319] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_32px_-12px_rgba(227,27,35,0.6)]">
              <PenLine size={16} /> Leave a review
            </button>
          </div>
        </Reveal>

        {/* Prominent review bar */}
        <Reveal delay={0.05}>
          <div data-testid="review-bar"
            className="mt-8 flex flex-col items-center justify-between gap-5 overflow-hidden rounded-3xl bg-[#5A189A] px-6 py-7 sm:px-10 md:flex-row"
            style={{ backgroundImage: "linear-gradient(120deg,#3D0A6E 0%,#5A189A 55%,#7B2CBF 100%)" }}>
            <div className="flex items-center gap-4 text-white">
              <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 sm:flex">
                <Star size={24} className="text-[#F4B400]" fill="currentColor" />
              </span>
              <div>
                <div className="font-display text-xl font-extrabold sm:text-2xl">Shipped with us? Leave a review.</div>
                <div className="text-sm text-white/70">Your feedback goes live once our team approves it.</div>
              </div>
            </div>
            <button data-testid="review-bar-btn" onClick={() => setOpen(true)}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#F4B400] px-7 py-3.5 text-sm font-bold text-[#1F1F1F] shadow-[0_12px_32px_-12px_rgba(0,0,0,0.5)] transition-transform hover:-translate-y-0.5">
              <PenLine size={17} /> Write a review
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <Carousel opts={{ loop: true, align: "start" }} className="mt-12" data-testid="reviews-carousel">
            <CarouselContent>
              {reviews.map((r, i) => (
                <CarouselItem key={i} className="md:basis-1/2">
                  <div className="card-soft h-full rounded-3xl p-8">
                    <div className="flex gap-1 text-[#F4B400]">
                      {Array.from({ length: r.rating || 5 }).map((_, s) => <Star key={s} size={18} fill="currentColor" />)}
                    </div>
                    <p className="mt-5 text-lg leading-relaxed text-[#1F1F1F]">"{r.quote}"</p>
                    <div className="mt-8 flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#5A189A] font-display text-sm font-bold text-white">
                        {r.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </span>
                      <div>
                        <div className="font-semibold text-[#1F1F1F]">{r.name}</div>
                        <div className="text-xs text-[#9ca3af]">{r.role}</div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="mt-8 flex items-center gap-3">
              <CarouselPrevious className="static translate-y-0 h-11 w-11 border-black/10 bg-white text-[#1F1F1F] hover:bg-[#5A189A] hover:text-white" data-testid="reviews-prev" />
              <CarouselNext className="static translate-y-0 h-11 w-11 border-black/10 bg-white text-[#1F1F1F] hover:bg-[#E31B23] hover:text-white" data-testid="reviews-next" />
            </div>
          </Carousel>
        </Reveal>
      </div>

      <ReviewDialog open={open} setOpen={setOpen} />
    </section>
  );
}
